import React, { useRef, useImperativeHandle, forwardRef, useState, useCallback } from 'react';
import './reportCards.css';

/**
 * SharedVideoPlayer - Reusable video player for all report cards
 * 
 * Features:
 * - Maintains playback state across card switches
 * - Timestamp navigation support
 * - Fit mode controls (contain, cover, actual)
 * - Overlay support for bounding boxes
 * 
 * Props:
 * - src: Video URL
 * - poster: Poster image URL
 * - fitMode: 'contain' | 'cover' | 'actual'
 * - onTimeUpdate: Callback for time updates
 * - className: Additional CSS classes
 * 
 * Ref Methods:
 * - seekTo(seconds): Jump to specific timestamp
 * - play(): Start playback
 * - pause(): Pause playback
 * - getCurrentTime(): Get current playback time
 */
const SharedVideoPlayer = forwardRef(({ 
  src, 
  poster, 
  fitMode = 'contain',
  onTimeUpdate,
  className = '',
  ...props 
}, ref) => {
  const videoRef = useRef(null);
  // eslint-disable-next-line no-unused-vars
  const [isPlaying, setIsPlaying] = useState(false); // Reserved for custom playback controls
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  // Expose methods to parent components via ref
  useImperativeHandle(ref, () => ({
    seekTo: (seconds) => {
      if (videoRef.current) {
        videoRef.current.currentTime = Math.max(0, Math.min(seconds, duration));
      }
    },
    play: () => {
      if (videoRef.current) {
        videoRef.current.play();
      }
    },
    pause: () => {
      if (videoRef.current) {
        videoRef.current.pause();
      }
    },
    getCurrentTime: () => {
      return videoRef.current ? videoRef.current.currentTime : 0;
    },
    getDuration: () => {
      return videoRef.current ? videoRef.current.duration : 0;
    },
    getVideoElement: () => videoRef.current
  }), [duration]);

  const handleTimeUpdate = useCallback(() => {
    if (videoRef.current) {
      const time = videoRef.current.currentTime;
      setCurrentTime(time);
      onTimeUpdate?.(time);
    }
  }, [onTimeUpdate]);

  const handleLoadedMetadata = useCallback(() => {
    if (videoRef.current) {
      setDuration(videoRef.current.duration);
    }
  }, []);

  const handlePlay = useCallback(() => {
    setIsPlaying(true);
  }, []);

  const handlePause = useCallback(() => {
    setIsPlaying(false);
  }, []);

  const getFitModeStyle = () => {
    switch (fitMode) {
      case 'cover':
        return { objectFit: 'cover' };
      case 'actual':
        return { objectFit: 'none' };
      case 'contain':
      default:
        return { objectFit: 'contain' };
    }
  };

  if (!src) {
    return (
      <div className={`rc-video-container ${className}`}>
        <div className="rc-photo-placeholder">
          No video available
        </div>
      </div>
    );
  }

  return (
    <div className={`rc-video-container ${className}`} style={{ width: '100%', height: 'auto' }}>
      <video
        ref={videoRef}
        className="rc-video-player"
        src={src}
        poster={poster}
        preload="metadata" // Load metadata to show poster/thumbnail immediately
        controls
        playsInline
        muted={false}
        webkit-playsinline="true"
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onPlay={handlePlay}
        onPause={handlePause}
        style={{ ...getFitModeStyle(), width: '100%', height: 'auto' }}
        {...props}
      />
      
      {/* Optional: Custom controls overlay could go here */}
      {process.env.NODE_ENV === 'development' && (
        <div style={{
          position: 'absolute',
          top: 8,
          left: 8,
          background: 'rgba(0,0,0,0.7)',
          color: 'white',
          padding: '4px 8px',
          borderRadius: '4px',
          fontSize: '12px',
          fontFamily: 'monospace'
        }}>
          {Math.round(currentTime)}s / {Math.round(duration)}s | {fitMode}
        </div>
      )}
    </div>
  );
});

SharedVideoPlayer.displayName = 'SharedVideoPlayer';

export default SharedVideoPlayer;
